import { Button, Card, Chip, Divider, Link, Stack, styled, SxProps, Typography } from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { assetService, ingestService, workflowService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { AdminAssetSingleRejectionDetails } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../Router/router";
import { UserHasAcl } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { ModalContext } from "../../Modals";
import ModalConfirmActionOpenButton from "../../Modals/ModalConfirmAction/modal-confirm-action-open-button";
import ModalEditMetadata, { ModalEditMetadataElementProps } from "../../Modals/ModalEditMetadata/modal-edit-metadata";
import ModalNewAssignment, {
  ModalNewAssignmentElementProps,
} from "../../Modals/ModalNewAssignment/modal-new-assignment";
import ModalOrderFileFixingOpenButton from "../../Modals/ModalOrderFileFixing/modal-order-file-fixing-open-button";
import ModalRequestPurgeOpenButton from "../../Modals/ModalPurgeAsset/modal-request-purge-open-button";

export interface AssetHeroRejectionCardProps {
  sx?: SxProps;
  assetId: number;
  assetParentTitleId: number | null;
  rejectionDetails: AdminAssetSingleRejectionDetails;
}

const AssetHeroRejectionCard: React.FC<AssetHeroRejectionCardProps> = (props) => {
  const { t } = useTranslation();
  const { sx = {}, assetId, assetParentTitleId, rejectionDetails } = props;

  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleDeleteAsset = (comment?: string) => {
    ingestService.DeleteRequest({ id: assetId, comment: comment || null }).then((response) => {
      if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else if (response.success) {
        enqueueCustomSnackbar(t("alert:success.assetRequestDeleted", { assetId }), "success");
        if (assetParentTitleId !== null) navigate(`${routePaths.title.path}${assetParentTitleId}`);
        else navigate(GlobalConst.DEFAULT_REDIRECT_PATH);
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message! }), "error");
    });
  };

  const handleBypassRejection = (comment?: string) => {
    workflowService.AssetByPassRejection(assetId, comment).then((response) => {
      if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else if (response.success)
        enqueueCustomSnackbar(t("alert:success.assetBypassRejectionSuccess", { assetId }), "success");
      else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message! }), "error");
    });
  };

  // modal management
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const openModalNewAssignment = () => {
    if (!openModal) return;
    openModal<ModalNewAssignmentElementProps>(ModalNewAssignment, {
      assetId,
      assetProviderId: rejectionDetails.assignedProviderId!,
    });
  };

  const openModalEditMetadataAndReprocess = () => {
    if (!openModal) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, {
          objects: [res.graph],
          onSuccess: () => {
            ingestService.ReprocessRequestAfterAutomatedRejection(assetId).then((response) => {
              if (response.success)
                enqueueCustomSnackbar(t("alert:success.assetIngestReprocess", { assetId }), "success");
              else
                enqueueCustomSnackbar(t("alert:error.errorReprocessRequest", { message: response.message }), "error");
            });
          },
        });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  return (
    <StyledCard variant="outlined" sx={sx}>
      <Stack direction="row" justifyContent="space-between">
        <MainContent justifyContent="space-between" spacing={2}>
          <Typography sx={{ mb: 1 }} fontWeight={600} variant="subtitle2">
            {t("component:asset.body.rejectionDetails")}
          </Typography>
          <Typography color="error" sx={{ maxHeight: 200, overflow: "auto" }}>
            {rejectionDetails.rejectedComment}
          </Typography>
          <Stack spacing={1}>
            <MetadataListItem title={t("menu:title.rejectedBy")}>
              {rejectionDetails.rejectedBy !== "System" ? (
                <Link href="#" underline="hover" fontWeight={500}>
                  {rejectionDetails.rejectedBy}
                </Link>
              ) : (
                rejectionDetails.rejectedBy
              )}
            </MetadataListItem>
            <MetadataListTextItem title={t("menu:title.rejectedAt")}>
              {rejectionDetails.rejectedAt}
            </MetadataListTextItem>
            <MetadataListItem title={t("common:provider")}>
              <Chip size="small" color="info" label={rejectionDetails.assignedProviderName} />
            </MetadataListItem>
          </Stack>
        </MainContent>
        <Divider orientation="vertical" sx={{ mr: 5, ml: 5, height: "auto" }} />
        <Actions>
          <ModalRequestPurgeOpenButton
            title={t("modal:title.confirmMediaPurgeAction")}
            assetIds={[assetId]}
            sx={{ mb: 1 }}
            size="small"
            startIcon={<RestartAltIcon />}
            variant="contained"
            disabled={!rejectionDetails.userCanPurge}
          >
            {t("common:requestPurge")}
          </ModalRequestPurgeOpenButton>
          {rejectionDetails.currentStatus === ObjectStatus.RejectedAuto && (
            <Button
              sx={{ mb: 1 }}
              size="small"
              startIcon={<EditIcon />}
              disabled={!rejectionDetails.userCanEditMetadata}
              onClick={openModalEditMetadataAndReprocess}
            >
              {t("common:editMetadataAndReprocess")}
            </Button>
          )}
          <Button
            sx={{ mb: 1 }}
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={openModalNewAssignment}
            disabled={!rejectionDetails.userCanAddNewAssignment || !UserHasAcl(AclTypes.aclMetadataUpdate)}
          >
            {t("common:newAssignment")}
          </Button>
          <ModalConfirmActionOpenButton
            displayComment
            modalMessage={t("modal:message.confirmRequestDeletion")}
            onConfirm={(comment?: string) => handleDeleteAsset(comment)}
            sx={{ mb: 1 }}
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={!rejectionDetails.userCanDeleteRequest}
          >
            {t("button:deleteRequests_one")}
          </ModalConfirmActionOpenButton>
          <ModalConfirmActionOpenButton
            displayComment
            modalMessage={t("modal:message.confirmRejectionBypass")}
            onConfirm={(comment?: string) => handleBypassRejection(comment)}
            size="small"
            color="error"
            startIcon={<VisibilityOffIcon />}
            disabled={!rejectionDetails.userCanBypassReject}
          >
            {t("modal:body.bypassRejection")}
          </ModalConfirmActionOpenButton>
          <ModalOrderFileFixingOpenButton fullWidth size="small" objectId={assetId} sx={{ mt: 1 }} />
        </Actions>
      </Stack>
    </StyledCard>
  );
};

export default AssetHeroRejectionCard;

const StyledCard = styled(Card)(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(3)} ${theme.spacing(3)}`,
  background: "linear-gradient(180deg, rgba(237, 98, 115, 0.05) 0%, rgba(237, 98, 115, 0) 100%)",
}));

const MainContent = styled(Stack)(() => ({
  flex: "1 1 auto",
}));

const Actions = styled(Stack)(() => ({
  width: "240px",
  flex: "0 0 auto",
}));
